import { SignableOfferForm } from '.';
import { SelectOption } from '../../../../components/Select';
import { SignableOffer } from '../../../../entities/v1/applicant_tracking/SignableOffer';
import { Signatory } from '../../../../entities/v1/applicant_tracking/Signatory';
import { AttachmentDto } from '../../../../services/dtos/EmailDto';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';

const DEFAULT_ATTACHMENTS = ['organization_logo.png'];

export function mapSignableOfferToForm(
  signableOffer: SignableOffer,
): SignableOfferForm {
  const [bonusCurrency, bonusValue] = getCurrencyAndValue(
    signableOffer?.variables?.bonus,
  );
  const [salaryCurrency, salaryValue] = getCurrencyAndValue(
    signableOffer?.variables?.salary,
  );

  return {
    attachments: filterAttachments(signableOffer),
    to: signableOffer?.email?.to[0],
    cc: signableOffer?.email?.cc?.map(convertToSelectOption),
    bcc: signableOffer?.email?.bcc?.map(convertToSelectOption),
    emailJson: signableOffer?.email?.bodyJson,
    subjectJson: signableOffer?.email?.subjectJson,
    signableOfferTemplate: signableOffer?.offerTemplate,
    signatory: signatoryUser(signableOffer),
    bonus: bonusValue,
    bonusCurrency: bonusCurrency || DEFAULT_CURRENCY.value,
    candidateName: convertToString(signableOffer?.variables?.candidateName),
    companyName: convertToString(signableOffer?.variables?.companyName),
    directManager: convertToString(signableOffer?.variables?.directManager),
    equity: convertToNumber(signableOffer?.variables?.equity),
    jobTitle: convertToString(signableOffer?.variables?.jobTitle),
    offerExpirationDate: convertToString(
      signableOffer?.variables?.offerExpirationDate,
    ),
    paidTimeOff: convertToNumber(signableOffer?.variables?.paidTimeOff),
    salary: salaryValue,
    salaryCurrency: salaryCurrency || DEFAULT_CURRENCY.value,
    senderName: convertToString(signableOffer?.variables?.senderName),
    sickDays: convertToNumber(signableOffer?.variables?.sickDays),
    startingDate: convertToString(signableOffer?.variables?.startingDate),
    todayDate: convertToString(signableOffer?.variables?.todayDate),
    workingHours: convertToNumber(signableOffer?.variables?.workingHours),
    customVariables: toMap(signableOffer?.variables),
  };
}

const convertToString = (value: any): string =>
  value ? (value as string) : null;
const convertToNumber = (value: string | number): number =>
  parseInt(`${value}`.replace(/,/g, ''));

const convertToSelectOption = (value: any): SelectOption => ({
  label: convertToString(value),
  value: value,
});

function getCurrencyAndValue(
  combinedAttribute: string | number,
): [string, number] {
  const parts = convertToString(combinedAttribute)?.split(' ');
  if (!parts) return [null, null];

  if (parts.length > 1) {
    return [parts[0], convertToNumber(parts[1])];
  }

  return [DEFAULT_CURRENCY.value, null];
}

function toMap(
  variables: Record<string, any> | Map<string, any> | undefined,
): Map<string, any> {
  if (!variables) {
    return new Map<string, any>();
  }
  if (variables instanceof Map) {
    return variables;
  }

  return new Map<string, any>(Object.entries(variables));
}

function signatoryUser(signableOffer: SignableOffer): Signatory {
  const nonCandidateSigningLink = signableOffer?.signingLinks.filter(
    (signatory) => signatory.userId,
  );

  return signableOffer?.offerTemplate.signatories?.find(
    (signatory) => signatory.userId === nonCandidateSigningLink[0].userId,
  );
}

function filterAttachments(signableOffer: SignableOffer): AttachmentDto[] {
  return (
    signableOffer?.email?.attachments?.filter(
      ({ filename }) => !DEFAULT_ATTACHMENTS.includes(filename),
    ) || []
  );
}
